import React, { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../../common/types';
import { useTRPCAPI, useTRPCQuery, useTRPCMutation } from '../../../../trpc/trpc';
import { useMusic24Capabilities } from '../../hooks/useMusic24Capabilities';
import { useUpgradePrompt } from '../../hooks/useUpgradePrompt';
import { SMLUpgradeFeature } from '../../config/music24PlanCapabilities';
import { SummaryReadyView } from './SummaryReadyView';
import { SummaryPendingView } from './SummaryPendingView';
import { SummaryDataNotReadyView } from './SummaryDataNotReadyView';
import { SummaryErrorView } from './SummaryErrorView';
import { SummaryLockedView } from './SummaryLockedView';
import { summaryLanguage } from './summaryLanguage';

interface Props {
  date: string;
}

const POLL_MS = 3000;

const TrendsSummaryPanelComponent: React.FC<Props> = ({ date }) => {
  const intl = useIntl();
  const trpcMusicAPI = useTRPCAPI();
  const language = summaryLanguage(intl.locale);
  const { aiInsightsPerMonth } = useMusic24Capabilities();
  const isLocked = aiInsightsPerMonth === 0;
  const openUpgradePrompt = useUpgradePrompt();
  const openAiInsightsPrompt = useCallback(() => openUpgradePrompt(SMLUpgradeFeature.aiInsights), [openUpgradePrompt]);

  const queryOptions = trpcMusicAPI.aiInsights.trendsSummary.get.queryOptions({ date, language });
  const { data, refetch } = useTRPCQuery({
    ...queryOptions,
    enabled: !isLocked,
    refetchInterval: (query) => (query.state.data?.status === 'pending' ? POLL_MS : false),
  });

  const retryMutation = useTRPCMutation(trpcMusicAPI.aiInsights.trendsSummary.retry.mutationOptions());

  const onRetry = useCallback(() => {
    retryMutation.mutate(
      { date, language },
      {
        onSuccess: () => {
          refetch().catch(console.error);
        },
      }
    );
  }, [retryMutation, date, language, refetch]);

  if (isLocked) return <SummaryLockedView onUpgrade={openAiInsightsPrompt} />;

  if (data === undefined) return <SummaryPendingView />;

  switch (data.status) {
    case 'ready':
      return <SummaryReadyView summary={data.summary} />;
    case 'pending':
      return <SummaryPendingView />;
    case 'data_not_ready':
      return <SummaryDataNotReadyView />;
    case 'error':
      return <SummaryErrorView onRetry={onRetry} />;
    default:
      return null;
  }
};

export const TrendsSummaryPanel = TypedMemo(TrendsSummaryPanelComponent);
